import { supabase } from "@/integrations/supabase/client";
import { getStudyUsage } from "./study-monetization.functions";
import { processStudyQuestion } from "./study-tutor.functions";
import { studyClientInfo } from "./study-client";
import type { StudyUsage } from "./study-policy";
import { getFunnelAttribution, trackEmailCampaignReturn } from "./study-conversion";
import { isEmailReactivation } from "./email-reactivation-policy";
import { processEmailCampaignQuestion } from "./email-campaign-tutor.functions";

/** Existing published/mobile callers stay on their current path until staged activation. */
export async function invokeTutor(body: Record<string, unknown>): Promise<{ data: any; error: unknown }> {
  try {
    const usage = await getStudyUsage();
    if (!usage.enforcementEnabled) {
      if (isEmailReactivation(getFunnelAttribution()) && (!body.action || body.action === "solve")) {
        await trackEmailCampaignReturn();
        const data = await processEmailCampaignQuestion({ data: { ...body, requestId: crypto.randomUUID() } as any });
        return { data, error: null };
      }
      return supabase.functions.invoke("solve-problem", { body });
    }
    // Retry the same submission key for five minutes, including lost responses.
    const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(body)));
    const fingerprint = Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, "0")).join("");
    const previous = tutorRequests.get(fingerprint);
    const requestId = previous && previous.until > Date.now() ? previous.id : crypto.randomUUID();
    tutorRequests.set(fingerprint, { id: requestId, until: Date.now() + 300000 });
    for (const [key, request] of tutorRequests) if (request.until <= Date.now()) tutorRequests.delete(key);
    const client = await studyClientInfo();
    const data = await processStudyQuestion({ data: { ...body, requestId, client } as any });
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
}
const tutorRequests = new Map<string, { id: string; until: number }>();

/**
 * Thrown when the solve/extract/practice edge function returns HTTP 429.
 * Carries the Retry-After hint (in seconds) so the UI can surface a
 * disabled/retry countdown instead of a generic error.
 */
export class RateLimitError extends Error {
  retryAfterSeconds: number;
  constructor(message: string, retryAfterSeconds: number) {
    super(message);
    this.name = "RateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

async function parseInvokeError(error: unknown): Promise<never> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (ctx instanceof Response) {
    let body: { error?: string; retryAfterSeconds?: number; rateLimited?: boolean } = {};
    try { body = await ctx.clone().json(); } catch { /* ignore */ }
    if (ctx.status === 429) {
      const header = ctx.headers.get("Retry-After");
      const retry = Number(header) || body.retryAfterSeconds || 60;
      throw new RateLimitError(body.error || "Too many requests. Please slow down.", retry);
    }
    if (body.error) throw new Error(body.error);
  }
  const msg = (error as { message?: string } | null)?.message ?? "Request failed";
  throw new Error(msg);
}

export interface SolutionData {
  concept: string;
  conceptDescription: string;
  steps: Array<{
    title: string;
    explanation: string;
    work?: string;
  }>;
  finalAnswer: string;
  summary: string;
  detectedLanguage?: string;
  originalConcept?: string;
  originalConceptDescription?: string;
  originalSteps?: Array<{
    title: string;
    explanation: string;
    work?: string;
  }>;
  originalFinalAnswer?: string;
  originalSummary?: string;
  usage?: { count: number; limit: number };
}

export interface ExtractedText {
  extractedText: string;
  problemType: "math" | "science" | "word_problem" | "other";
  confidence: "high" | "medium" | "low";
  formattedProblem: string;
  detectedLanguage?: string;
  usage?: { count: number; limit: number } & Partial<StudyUsage>;
}

export async function extractTextFromImage(imageData: string): Promise<ExtractedText> {
  const { data, error } = await invokeTutor({ question: imageData, action: "extract" });

  if (error) await parseInvokeError(error);
  if (data?.error) throw new Error(data.error);
  return data as ExtractedText;
}

export async function solveProblem(
  question: string,
  questionType: "text" | "image",
  outputLanguage = "en"
): Promise<SolutionData> {
  const { data, error } = await invokeTutor({ question, questionType, outputLanguage });

  if (error) await parseInvokeError(error);
  if (data?.error) {
    throw new Error(data.error);
  }

  return data as SolutionData;
}

export async function checkAndIncrementScan(userId: string): Promise<{ allowed: boolean; count: number; limit: number }> {
  const { data, error } = await supabase.rpc("check_and_increment_scan", {
    p_user_id: userId,
  });

  if (error) throw new Error(error.message || "Failed to check scan usage");

  const result = data as { allowed: boolean; count: number; limit: number };
  return {
    allowed: result.allowed,
    count: result.count,
    limit: result.limit === -1 ? Infinity : result.limit,
  };
}

export async function saveSolvedProblem(
  userId: string,
  question: string,
  questionType: string,
  solution: SolutionData
) {
  await supabase.from("solved_problems").insert({
    user_id: userId,
    question,
    question_type: questionType,
    concept: solution.concept,
    solution: solution as any,
    final_answer: solution.finalAnswer,
  });

  // Feed the central Progress Engine (XP, level, goals, coach insights).
  const { recordProgressEvent } = await import("./progress");
  await recordProgressEvent({
    type: "problem_solved",
    subject: solution.concept ?? questionType,
    metadata: { question_type: questionType },
  });
}

export async function explainStepDeeper(params: {
  stepTitle: string;
  stepExplanation: string;
  stepWork?: string;
  originalQuestion?: string;
  outputLanguage?: string;
}): Promise<{ deeperExplanation: string }> {
  const { data, error } = await invokeTutor({
      action: "explain_deeper",
      stepTitle: params.stepTitle,
      stepExplanation: params.stepExplanation,
      stepWork: params.stepWork,
      originalQuestion: params.originalQuestion,
      outputLanguage: params.outputLanguage ?? "en",
  });
  if (error) await parseInvokeError(error);
  if (data?.error) throw new Error(data.error);
  return data as { deeperExplanation: string };
}

/** Builds a compact text summary of a solution to give the AI context. */
export function solutionToContext(solution: SolutionData): string {
  const steps = solution.steps
    .map((s, i) => `${i + 1}. ${s.title}: ${s.explanation}${s.work ? ` [${s.work}]` : ""}`)
    .join("\n");
  return `Concept: ${solution.concept}\n${solution.conceptDescription}\n\nSteps:\n${steps}\n\nFinal answer: ${solution.finalAnswer}\nSummary: ${solution.summary}`;
}

/**
 * Answers a free-form follow-up question about the solution currently on
 * screen. Under staged enforcement each successful tutor reply uses one question.
 */
export async function askFollowUp(params: {
  followUpQuestion: string;
  originalQuestion?: string;
  solutionContext?: string;
  outputLanguage?: string;
}): Promise<{ answer: string }> {
  const { data, error } = await invokeTutor({
      action: "follow_up",
      followUpQuestion: params.followUpQuestion,
      originalQuestion: params.originalQuestion,
      solutionContext: params.solutionContext,
      outputLanguage: params.outputLanguage ?? "en",
  });
  if (error) await parseInvokeError(error);
  if (data?.error) throw new Error(data.error);
  return data as { answer: string };
}
